import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_BASE from '../utils/api';
import { playNotificationSound, requestNotificationPermission, showBrowserNotification } from '../utils/notificationSound';

const POLL_INTERVAL = 30000; // 30 seconds

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const prevCountRef = useRef(0);
  const dropdownRef = useRef(null);
  const token = localStorage.getItem('token');

  // Track user interaction (required for audio playback on mobile)
  useEffect(() => {
    const handleInteraction = () => setHasInteracted(true);
    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('touchstart', handleInteraction, { once: true });
    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (!token || !user) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let newCount;
      let notifications = [];

      if (user.role === 'parent') {
        const res = await axios.get(`${API_BASE}/communications/my-notifications`, { headers });
        newCount = res.data.unreadCount || 0;
        notifications = (res.data.notifications || []).filter(n => !n.read).slice(0, 5);
      } else {
        const [countRes, listRes] = await Promise.all([
          axios.get(`${API_BASE}/notifications/unread-count`, { headers }),
          axios.get(`${API_BASE}/notifications`, { headers, params: { unreadOnly: true } })
        ]);
        newCount = countRes.data.count || 0;
        notifications = (listRes.data || []).slice(0, 5);
      }

      setRecentNotifications(notifications);

      // Play sound + browser notification when count increases
      if (newCount > prevCountRef.current && prevCountRef.current !== 0) {
        if (hasInteracted) {
          playNotificationSound();
        }
        const diff = newCount - prevCountRef.current;
        showBrowserNotification(
          'New School Notification',
          `You have ${diff} new notification${diff > 1 ? 's' : ''}`
        );
      }

      prevCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch (err) {
      console.error('Failed to fetch notification count:', err);
    }
  }, [token, user, hasInteracted]);

  // Poll for new notifications
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleBellClick = () => {
    if (hasInteracted) {
      // Enable audio context on first bell click
      playNotificationSound();
    }
    setShowDropdown(!showDropdown);
  };

  const handleViewAll = () => {
    setShowDropdown(false);
    if (user?.role === 'parent') {
      navigate('/parent/notifications');
    } else if (user?.role === 'admin' || user?.role === 'super_admin') {
      navigate('/admin/communications');
    }
  };

  const markAsRead = async (id) => {
    try {
      const endpoint = user?.role === 'parent'
        ? `${API_BASE}/communications/${id}/read`
        : `${API_BASE}/notifications/${id}/read`;
      await axios.put(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUnreadCount();
    } catch (err) {
      console.error(err);
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleBellClick}
        className="relative p-2 text-gray-600 hover:text-indigo-600 hover:bg-gray-100 rounded-full transition-colors"
        title="Notifications"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          <div className="p-3 bg-indigo-50 border-b flex justify-between items-center">
            <h3 className="font-semibold text-indigo-800">Notifications</h3>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {unreadCount} new
              </span>
            )}
          </div>

          <div className="max-h-72 overflow-y-auto">
            {recentNotifications.length > 0 ? (
              recentNotifications.map(n => (
                <div
                  key={n._id}
                  onClick={() => markAsRead(n._id)}
                  className="p-3 border-b hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-lg mt-0.5">
                      {n.channel === 'sms' ? '📱' : n.channel === 'whatsapp' ? '💬' : '🔔'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-800 truncate">{n.title}</p>
                      <p className="text-xs text-gray-500 line-clamp-2">{n.message}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(n.createdAt).toLocaleString()}
                      </p>
                    </div>
                    {!n.read && <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 shrink-0"></span>}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-gray-400">
                <p className="text-2xl mb-1">🔔</p>
                <p className="text-sm">No new notifications</p>
              </div>
            )}
          </div>

          <div className="p-2 bg-gray-50 border-t">
            <button
              onClick={handleViewAll}
              className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-medium py-1.5 rounded hover:bg-indigo-50 transition-colors"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
